import { useLocalStorage } from '@mantine/hooks';
import { TSessionConfigData } from 'providers/contexts/CosmosContext';
import { useMemo } from 'react';
import { hasBetaProgram, hasOpenSearchEditorialExp } from 'utility/experiments';
import {
  BaseUserAccountFull,
  GetUserMeData,
  SessionConfigData,
} from 'utility/types/dataProvider';

const EDITORIAL_AI_SEARCH_LOCAL_STORAGE_KEY = 'blox:ai-search:editorial:active';

// Read directly from local storage - for initial state checks.
// No export: All other areas must be utilizing the reactive hook.
function readEditorialAiSearchEnabled(): boolean {
  try {
    const value = localStorage.getItem(EDITORIAL_AI_SEARCH_LOCAL_STORAGE_KEY);
    return value === 'true';
  } catch {
    return false;
  }
}

/**
 * Resources that support AI Search (semantic param).
 * Currently only 'editorial' supports AI Search.
 */
type ResourceSupportsOpenSearchArgs = {
  resource?: string | null;
};

export function resourceSupportsOpenSearch({
  resource,
}: ResourceSupportsOpenSearchArgs): boolean {
  if (!resource || typeof resource !== 'string') {
    return false;
  }
  const supported = ['editorial'];
  const isSupported = supported.includes(resource);
  return isSupported;
}

/**
 * Check for both OpenSearch experiments and Beta access.
 * - Requires all three experiments to be enabled.
 * - Requires the user to be in the Beta Program.
 */
type HasAiSearchEditorialAccessArgs = {
  sc?: TSessionConfigData;
  userInfo?: GetUserMeData;
};

export function hasOpenSearchEditorialAccess({
  sc,
  userInfo,
}: HasAiSearchEditorialAccessArgs): boolean {
  const hasBetaAccess = hasBetaProgram(sc, userInfo);
  const hasOpenSearchExps = hasOpenSearchEditorialExp(sc);
  const isEnabled = hasBetaAccess && hasOpenSearchExps;
  return isEnabled;
}

type UseEditorialAiSearchArgs = {
  userInfo?: BaseUserAccountFull | undefined;
  sessionConfig?: SessionConfigData | undefined;
  resource?: string | null;
};

type UseEditorialAiSearchResult = {
  aiSearchEnabledInStorage: boolean;
  setAiSearchEnabledInStorage: React.Dispatch<React.SetStateAction<boolean>>;
  isEditorialAndActive: boolean;
  isEnabledAndActive: boolean;
  hasAiSearchPrerequisites: boolean;
};

export const useEditorialAiSearchState = ({
  resource,
  userInfo,
  sessionConfig,
}: UseEditorialAiSearchArgs): UseEditorialAiSearchResult => {
  /**
   * NOTE: This is JUST the local storage value...
   * You will have to combine resource + exps + beta in in order
   * to determine if AI Search is actually enabled for the application.
   */
  const [aiSearchEnabledInStorage, setAiSearchEnabledInStorage] =
    useLocalStorage<boolean>({
      key: EDITORIAL_AI_SEARCH_LOCAL_STORAGE_KEY,
      defaultValue: readEditorialAiSearchEnabled(),
    });

  /**
   * This is for some areas like LeftNav that adjust their shortcuts.
   * Is it ACTUALLY enabled for the application? (LS true + experiments w/ beta)
   * You must check the local storage value PLUS the experimental states.
   * Otherwise, if a user turns off the experiments but never turns off
   * the local storage value, parts of app will still treat it as enabled.
   */
  const isEnabledAndActive = useMemo(() => {
    const hasExpsAndBeta = hasOpenSearchEditorialAccess({
      sc: sessionConfig,
      userInfo: userInfo,
    });
    const isEnabled = aiSearchEnabledInStorage && hasExpsAndBeta;
    return isEnabled;
  }, [aiSearchEnabledInStorage, sessionConfig, userInfo]);

  /**
   * Editorial Search Helpers
   */

  // Editorial resource, enabled in LS, with experiments/beta checks.
  const isEditorialAndActive = useMemo(() => {
    return resource === 'editorial' && isEnabledAndActive;
  }, [resource, isEnabledAndActive]);

  // Editorial resource and has experiments/beta checks?
  const hasAiSearchPrerequisites = useMemo(() => {
    const supported = resourceSupportsOpenSearch({ resource: resource });
    const access = hasOpenSearchEditorialAccess({
      sc: sessionConfig,
      userInfo: userInfo,
    });
    const isEnabled = supported && access;
    return isEnabled;
  }, [resource, sessionConfig, userInfo]);

  /**
   * Final hook return value
   */
  const hookValue = useMemo(() => {
    return {
      aiSearchEnabledInStorage,
      setAiSearchEnabledInStorage,
      isEditorialAndActive,
      isEnabledAndActive,
      hasAiSearchPrerequisites,
    };
  }, [
    aiSearchEnabledInStorage,
    isEditorialAndActive,
    isEnabledAndActive,
    setAiSearchEnabledInStorage,
    hasAiSearchPrerequisites,
  ]);

  return hookValue;
};
